import { HttpStatusCode } from '@angular/common/http';
import { BookingFailureKind } from '@models/booking-outcome.model';

/**
 * Classifies a failed `/book` request by its HTTP status (Angular reports every failure as an
 * HttpErrorResponse carrying one).
 *
 * A LOST answer is not a refusal: status 0 (the connection dropped) and 502/504 (a gateway gave up
 * on - or got garbage from - a backend that may well have finished) say nothing about whether the
 * booking was made. A 4xx is the backend's verdict on this booking. Every other failure is the
 * backend's own trouble and may pass on a retry.
 *
 * One status 0 is NOT a lost answer: the app's HTTP interceptor re-throws a 200 whose body carries
 * `errors` as an HttpErrorResponse with `status: errors[0].code`, which is 0 when the backend sent no
 * code, and with an `Error` as its payload. That booking was answered - and refused.
 *
 * Nor is a 423 (booking lock) a verdict: another request for the same slot held the lock, and after a
 * payment that is most likely this payment's own first request, which is booking. cakephp refunds
 * nothing on a lock, so no refund may be promised - and no new payment either: the booking is being
 * completed.
 */
export function classifyBookingFailure(error: { status?: number; error?: unknown } | null | undefined): BookingFailureKind {
  const status = error?.status;
  if (status === 0 && error?.error instanceof Error) {
    return 'refused';
  }
  if (status === HttpStatusCode.Locked) {
    return 'completing';
  }
  if (status === 0 || status === HttpStatusCode.BadGateway || status === HttpStatusCode.GatewayTimeout) {
    return 'unknown';
  }
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return 'refused';
  }

  return 'transient';
}

/**
 * Whether the summary offers "Pay again" - a NEW payment - after a paid booking failed this way. Only
 * when the customer cannot know whether a booking exists and must be able to pay once they have
 * checked their email (`unknown`, `spent`). Never for `completing`: that payment is taken and its
 * booking is being completed, so a second payment would charge the customer twice.
 */
export function offersPayAgain(failure: BookingFailureKind | null | undefined): boolean {
  return failure === 'unknown' || failure === 'spent';
}
